/**
 * @fileoverview Generic entity table component with CRUD actions
 * Following DMS component patterns from the framework
 */
import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import {
  DmsTable,
  DmsButtonGroup,
  DmsFlexContainer,
  DmsButton,
  DmsExport,
  intl,
} from "dmsbasereact";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";

/**
 * Default action buttons renderer
 */
const defaultActionsRenderer = ({
  row,
  onView,
  onDelete,
  onToggleStatus,
  onBlock,
  onUnblock,
  isActive,
  viewTitle,
  deleteTitle,
  activateTitle,
  deactivateTitle,
  blockTitle,
  unblockTitle,
  isMobile,
}) => (
  <DmsButtonGroup
    sx={{
      '& .MuiIconButton-root, & .MuiButton-root': {
        minHeight: isMobile ? '44px' : '36px',
        minWidth: isMobile ? '44px' : '36px',
      }
    }}
  >
    {onView && (
      <DmsButton
        icon={<VisibilityIcon />}
        title={viewTitle || intl.get("ViewDetails").d("View Details")}
        onClick={() => onView(row)}
      />
    )}
    {onDelete && (
      <DmsButton
        icon={<DeleteIcon />}
        title={deleteTitle || intl.get("Delete").d("Delete")}
        onClick={() => onDelete(row)}
        color="error"
      />
    )}
    {onToggleStatus && (
      <DmsButton
        icon={isActive ? <BlockIcon /> : <LockOpenIcon />}
        title={isActive
          ? (deactivateTitle || intl.get("Deactivate").d("Deactivate"))
          : (activateTitle || intl.get("Activate").d("Activate"))}
        onClick={() => onToggleStatus(row)}
        color={isActive ? "warning" : "success"}
      />
    )}
    {(onBlock || onUnblock) && (
      isActive ? (
        onBlock && (
          <DmsButton
            icon={<BlockIcon />}
            title={blockTitle || intl.get("Block").d("Block")}
            onClick={() => onBlock(row)}
            color="warning"
          />
        )
      ) : (
        onUnblock && (
          <DmsButton
            icon={<LockOpenIcon />}
            title={unblockTitle || intl.get("Unblock").d("Unblock")}
            onClick={() => onUnblock(row)}
            color="success"
          />
        )
      )
    )}
  </DmsButtonGroup>
);

/**
 * Generic entity table component
 *
 * @example
 * <DmsEntityTable
 *   ref={tableRef}
 *   url="/patient/table"
 *   columns={[
 *     { label: "Name", column_name: "name", filter: true },
 *     { label: "CPF", column_name: "cpf", filter: true },
 *   ]}
 *   onView={handleView}
 *   onDelete={handleDelete}
 *   onToggleStatus={handleToggleStatus}
 *   isActiveCheck={(row) => row.status === 1}
 *   onCreate={handleOpenCreateModal}
 *   createButtonLabel="Create Patient"
 * />
 */
const DmsEntityTable = forwardRef(({
  // Table configuration
  url,
  columns,
  preFilter = null,
  // Action handlers
  onView = null,
  onDelete = null,
  onToggleStatus = null,
  onBlock = null,
  onUnblock = null,
  onCreate = null,
  // Status check function
  isActiveCheck = null,
  // Labels
  createButtonLabel = null,
  viewTitle = null,
  deleteTitle = null,
  activateTitle = null,
  deactivateTitle = null,
  blockTitle = null,
  unblockTitle = null,
  actionsRenderer = null,
  actionsWidth = "150px",
  hideExport = false,
  hidePdf = false,
  hideCsv = false,
  hideExcel = false,
  hideFormats = [],
  tableProps = {},
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Build header with columns and actions
   */
  const header = useMemo(() => {
    const hasActions = onView || onDelete || onToggleStatus || onBlock || onUnblock;

    const baseColumns = columns.map(col => {
      const responsiveWidth = isMobile && col.width
        ? undefined
        : col.width;

      return {
        ...col,
        label: typeof col.label === "string" ? col.label : col.label,
        width: responsiveWidth,
      };
    });

    if (!hasActions) {
      return baseColumns;
    }

    const actionsColumn = {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: isMobile ? "180px" : (actionsWidth || "150px"),
      sortable: false,
      element: (row) => {
        const isActive = isActiveCheck ? isActiveCheck(row) : true;

        if (actionsRenderer) {
          return actionsRenderer({
            row,
            onView,
            onDelete,
            onToggleStatus,
            onBlock,
            onUnblock,
            isActive,
            isMobile,
          });
        }

        return defaultActionsRenderer({
          row,
          onView,
          onDelete,
          onToggleStatus,
          onBlock,
          onUnblock,
          isActive,
          viewTitle,
          deleteTitle,
          activateTitle,
          deactivateTitle,
          blockTitle,
          unblockTitle,
          isMobile,
        });
      },
    };

    return [...baseColumns, actionsColumn];
  }, [
    columns,
    onView,
    onDelete,
    onToggleStatus,
    onBlock,
    onUnblock,
    isActiveCheck,
    actionsRenderer,
    actionsWidth,
    viewTitle,
    deleteTitle,
    activateTitle,
    deactivateTitle,
    blockTitle,
    unblockTitle,
    isMobile,
  ]);

  /**
   * Build toolbar with create button and export
   */
  const toolbar = useMemo(() => {
    if (!onCreate && (!ref || hideExport)) return null;

    return (
      <DmsFlexContainer>
        <DmsFlexContainer.Left>
          {onCreate && (
            <DmsButton
              icon={<AddIcon />}
              onClick={onCreate}
              text={createButtonLabel || intl.get("Create").d("Create")}
              sx={{
                minHeight: isMobile ? '44px' : '36px',
              }}
            />
          )}
        </DmsFlexContainer.Left>
        <DmsFlexContainer.Right>
          {!hideExport && (
            <DmsExport
              table={ref}
              hidePdf={hidePdf}
              hideCsv={hideCsv}
              hideExcel={hideExcel}
              hideFormats={hideFormats}
            />
          )}
        </DmsFlexContainer.Right>
      </DmsFlexContainer>
    );
  }, [onCreate, createButtonLabel, ref, isMobile, hideExport, hidePdf, hideCsv, hideExcel, hideFormats]);

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        ...(isMobile && {
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
          },
        }),
      }}
    >
      <DmsTable
        ref={ref}
        url={url}
        header={header}
        needs_authentication={true}
        managed={true}
        toolbar={toolbar}
        export_table={!hideExport}
        pre_filter={preFilter}
        {...tableProps}
        sx={{
          minWidth: isMobile ? '600px' : 'auto',
          ...tableProps?.sx,
        }}
      />
    </Box>
  );
});

DmsEntityTable.displayName = "DmsEntityTable";

DmsEntityTable.propTypes = {
  /** API endpoint for table data */
  url: PropTypes.string.isRequired,
  /** Column definitions array */
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    column_name: PropTypes.string.isRequired,
    filter: PropTypes.bool,
    sortable: PropTypes.bool,
    width: PropTypes.string,
    element: PropTypes.func,
    function: PropTypes.func,
  })).isRequired,
  /** Pre-filter for table data */
  preFilter: PropTypes.array,
  /** Handler for viewing entity details */
  onView: PropTypes.func,
  /** Handler for deleting entity */
  onDelete: PropTypes.func,
  /** Handler for toggling entity status (single handler for both directions) */
  onToggleStatus: PropTypes.func,
  /** Handler for blocking entity (separate from unblock) */
  onBlock: PropTypes.func,
  /** Handler for unblocking entity (separate from block) */
  onUnblock: PropTypes.func,
  /** Handler for creating new entity */
  onCreate: PropTypes.func,
  /** Function to check if entity is active - (row) => boolean */
  isActiveCheck: PropTypes.func,
  /** Label for create button */
  createButtonLabel: PropTypes.string,
  /** Title for view action button */
  viewTitle: PropTypes.string,
  /** Title for delete action button */
  deleteTitle: PropTypes.string,
  /** Title for activate action button */
  activateTitle: PropTypes.string,
  /** Title for deactivate action button */
  deactivateTitle: PropTypes.string,
  /** Title for block action button */
  blockTitle: PropTypes.string,
  /** Title for unblock action button */
  unblockTitle: PropTypes.string,
  /** Custom actions renderer - (props) => JSX */
  actionsRenderer: PropTypes.func,
  /** Width for actions column */
  actionsWidth: PropTypes.string,
  /** Hide export button */
  hideExport: PropTypes.bool,
  /** Hide PDF export option */
  hidePdf: PropTypes.bool,
  /** Hide CSV export option */
  hideCsv: PropTypes.bool,
  /** Hide Excel export option */
  hideExcel: PropTypes.bool,
  /** Array of format strings to hide (flexible approach) */
  hideFormats: PropTypes.arrayOf(PropTypes.string),
  /** Additional props to pass to DmsTable */
  tableProps: PropTypes.object,
};


export { DmsEntityTable };
export default DmsEntityTable;
